import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LifeBuoy, X, Clock, Trash2, ArrowRight, CloudOff } from "lucide-react";
import { toast } from "sonner";
import { kvGet, kvSet, kvKeys, removeLocal } from "@/lib/localStore";

const DRAFT_PREFIX = "psc_inspection_draft_";
const RECOVERY_PREFIX = "psc_inspection_recovery_";
const QUEUE_KEY = "psc_offline_queue_v1";

export const collectLocalDrafts = async () => {
  const map = {};
  const add = (id, source, payload, savedAt) => {
    if (!map[id]) map[id] = { id, sources: [], payload: null, savedAt: 0 };
    map[id].sources.push(source);
    if (savedAt >= map[id].savedAt) {
      map[id].savedAt = savedAt;
      if (payload) map[id].payload = payload;
    }
  };
  const typeOf = (k) => (k.startsWith(DRAFT_PREFIX) ? ["draft", k.slice(DRAFT_PREFIX.length)]
    : k.startsWith(RECOVERY_PREFIX) ? ["recovery", k.slice(RECOVERY_PREFIX.length)] : [null, null]);

  // IndexedDB entries (primary store)
  try {
    const keys = await kvKeys();
    for (const k of keys) {
      const [type, id] = typeOf(String(k));
      if (!type) continue;
      const d = await kvGet(k);
      if (d) add(id, type, d.payload, d.savedAt || 0);
    }
    const q = (await kvGet(QUEUE_KEY)) || {};
    Object.entries(q).forEach(([id, entry]) => add(id, "unsynced", entry?.payload, entry?.at || 0));
  } catch { /* IndexedDB unavailable */ }

  // Legacy localStorage entries (older app versions)
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      const [type, id] = typeOf(k);
      if (type) {
        try {
          const d = JSON.parse(localStorage.getItem(k));
          add(id, type, d?.payload, d?.savedAt || 0);
        } catch { /* corrupt entry */ }
      } else if (k === QUEUE_KEY) {
        try {
          const q = JSON.parse(localStorage.getItem(k) || "{}");
          Object.entries(q).forEach(([qid, entry]) => add(qid, "unsynced", entry?.payload, entry?.at || 0));
        } catch { /* corrupt entry */ }
      }
    }
  } catch { /* localStorage unavailable */ }

  return Object.values(map)
    .filter((d) => d.payload && (Object.keys(d.payload.items || {}).length > 0 || (d.payload.title || "").trim()))
    .sort((a, b) => b.savedAt - a.savedAt);
};

export const discardLocalDraft = async (id) => {
  await removeLocal(DRAFT_PREFIX + id);
  await removeLocal(RECOVERY_PREFIX + id);
  try {
    const q = (await kvGet(QUEUE_KEY)) || {};
    if (q[id]) {
      delete q[id];
      await kvSet(QUEUE_KEY, q);
    }
  } catch { /* noop */ }
  try {
    const lq = JSON.parse(localStorage.getItem(QUEUE_KEY) || "{}");
    if (lq[id]) { delete lq[id]; localStorage.setItem(QUEUE_KEY, JSON.stringify(lq)); }
  } catch { /* noop */ }
};

const timeAgo = (ts) => {
  if (!ts) return "unknown time";
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return `${Math.floor(s / 86400)} day${s >= 172800 ? "s" : ""} ago`;
};

const SOURCE_LABEL = {
  draft: { text: "Saved on device", cls: "bg-emerald-100 text-emerald-800" },
  recovery: { text: "Session recovery", cls: "bg-amber-100 text-amber-800" },
  unsynced: { text: "Waiting to sync", cls: "bg-sky-100 text-sky-800" },
};

export const RecoveryDrawer = () => {
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [drafts, setDrafts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    collectLocalDrafts().then((d) => { if (alive) setDrafts(d); });
    return () => { alive = false; };
  }, [open, tick]);

  useEffect(() => {
    const onStorage = () => setTick((n) => n + 1);
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!drafts.length && !open) return null;

  return (
    <>
      {drafts.length > 0 && (
        <button
          data-testid="recovery-drawer-open"
          onClick={() => setOpen(true)}
          className="w-full mb-4 flex items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-left hover:bg-amber-100 transition-colors"
        >
          <LifeBuoy size={18} className="text-amber-600 shrink-0" />
          <span className="text-sm font-semibold text-amber-900 flex-1">
            {drafts.length} inspection draft{drafts.length > 1 ? "s" : ""} saved on this device
          </span>
          <span className="text-xs font-bold text-amber-700 inline-flex items-center gap-1">
            Recover <ArrowRight size={13} />
          </span>
        </button>
      )}

      {open && (
        <div data-testid="recovery-drawer-overlay" className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={() => setOpen(false)}>
          <div
            data-testid="recovery-drawer-panel"
            className="h-full w-full max-w-md bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <h2 className="font-bold text-[#1E1B4B] flex items-center gap-2"><LifeBuoy size={17} /> Recovery Drawer</h2>
                <p className="text-xs text-slate-500 mt-0.5">Inspection work saved on this device — nothing here is lost.</p>
              </div>
              <button data-testid="recovery-drawer-close" onClick={() => setOpen(false)} className="h-9 w-9 inline-flex items-center justify-center rounded-lg hover:bg-slate-100">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {drafts.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <CloudOff size={28} className="mx-auto mb-2" />
                  <p className="text-sm">No local drafts — everything is synced.</p>
                </div>
              )}
              {drafts.map((d) => {
                const answered = Object.keys(d.payload?.items || {}).length;
                return (
                  <div key={d.id} data-testid={`recovery-draft-${d.id}`} className="rounded-xl border border-slate-200 p-4 space-y-2">
                    <p className="font-semibold text-sm text-[#1E1B4B] break-words">
                      {(d.payload?.title || "").trim() || `Inspection ${d.id.slice(0, 8)}`}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {[...new Set(d.sources)].map((s) => (
                        <span key={s} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${SOURCE_LABEL[s].cls}`}>{SOURCE_LABEL[s].text}</span>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock size={12} /> {timeAgo(d.savedAt)} · {answered} answer{answered === 1 ? "" : "s"} captured
                    </p>
                    <div className="flex gap-2 pt-1">
                      <button
                        data-testid={`recovery-resume-${d.id}`}
                        onClick={() => { setOpen(false); navigate(`/inspections/${d.id}`); }}
                        className="flex-1 h-9 rounded-lg bg-[#1E1B4B] text-white text-xs font-bold hover:opacity-90"
                      >
                        Resume inspection
                      </button>
                      <button
                        data-testid={`recovery-discard-${d.id}`}
                        onClick={async () => {
                          if (!window.confirm("Discard this local draft? Any unsynced answers in it will be permanently removed from this device.")) return;
                          await discardLocalDraft(d.id);
                          setTick((n) => n + 1);
                          toast.success("Local draft discarded");
                        }}
                        className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RecoveryDrawer;
